import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldAlert, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";

/**
 * Admin — audit trail for spoofed checkout attempts.
 *
 * The /api/payments/checkout endpoint refuses (HTTP 400) any request
 * whose `user_id` or `customer_email` doesn't match the authenticated
 * caller — and logs the attempt into `suspicious_checkout_attempts`.
 *
 * This page surfaces that collection so ops can spot users trying to
 * abuse the endpoint (e.g. sending someone else's `user_id` to bill
 * them, or spoofing `customer_email` on a Stripe session).
 */
export default function AdminSuspiciousCheckouts() {
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState({ total: 0, distinct_users: 0 });
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const load = async () => {
        setRefreshing(true);
        try {
            const { data } = await api.get("/admin/suspicious-checkout-attempts");
            setRows(data.items || []);
            setSummary({
                total: data.total || 0,
                distinct_users: data.distinct_users || 0,
            });
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    if (loading) {
        return (
            <div className="flex items-center gap-2 text-navy-500 py-16 justify-center">
                <Loader2 className="animate-spin" size={16} />
                Loading audit trail…
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="admin-suspicious-checkouts-page">
            <header className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="text-xs uppercase tracking-[0.3em] text-gold-600 mb-2">
                        Security · audit trail
                    </div>
                    <h1 className="font-display text-4xl text-navy-700">
                        Suspicious checkouts
                    </h1>
                    <p className="text-navy-500 mt-2 max-w-xl leading-relaxed">
                        Every checkout request whose <span className="font-mono text-xs">user_id</span> or
                        <span className="font-mono text-xs"> customer_email</span> didn&apos;t match the
                        authenticated caller is logged here. Requests never reach Stripe — but the
                        attempt itself is worth reviewing for potential account abuse.
                    </p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                    <div className="rounded-2xl border border-navy-100 bg-cream px-5 py-3">
                        <div className="text-[10px] uppercase tracking-widest text-navy-500">
                            Total events
                        </div>
                        <div
                            data-testid="suspicious-total"
                            className="font-display text-3xl text-navy-700"
                        >
                            {summary.total}
                        </div>
                    </div>
                    <div className="rounded-2xl border border-navy-100 bg-cream px-5 py-3">
                        <div className="text-[10px] uppercase tracking-widest text-navy-500">
                            Distinct users
                        </div>
                        <div
                            data-testid="suspicious-distinct-users"
                            className="font-display text-3xl text-navy-700"
                        >
                            {summary.distinct_users}
                        </div>
                    </div>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={load}
                        disabled={refreshing}
                        data-testid="suspicious-refresh"
                        className="rounded-full border-navy-200 text-navy-700 h-10"
                    >
                        <RefreshCw
                            size={14}
                            className={`mr-2 ${refreshing ? "animate-spin" : ""}`}
                        />
                        Refresh
                    </Button>
                </div>
            </header>

            {rows.length === 0 ? (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-6 py-10 flex flex-col items-center text-center gap-3">
                    <ShieldAlert className="text-emerald-600" size={28} />
                    <div className="text-navy-700 font-medium">
                        No suspicious attempts logged.
                    </div>
                    <div className="text-sm text-navy-500 max-w-md">
                        The checkout endpoint is spoof-protected — if a bad actor tries
                        anything, you&apos;ll see them here.
                    </div>
                </div>
            ) : (
                <div className="rounded-2xl border border-navy-100 bg-white overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-cream/60 text-[11px] uppercase tracking-widest text-navy-500">
                            <tr>
                                <th className="text-left px-5 py-3">When</th>
                                <th className="text-left px-5 py-3">User</th>
                                <th className="text-left px-5 py-3">Field</th>
                                <th className="text-left px-5 py-3">Sent</th>
                                <th className="text-left px-5 py-3">Expected</th>
                                <th className="text-left px-5 py-3">Context</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-navy-100">
                            {rows.map((r) => (
                                <tr
                                    key={r.id}
                                    data-testid={`suspicious-row-${r.id}`}
                                    className="hover:bg-cream/30 transition-colors align-top"
                                >
                                    <td className="px-5 py-4 text-[11px] text-navy-500 whitespace-nowrap">
                                        {r.created_at
                                            ? new Date(r.created_at).toLocaleString()
                                            : "—"}
                                    </td>
                                    <td className="px-5 py-4">
                                        <div className="font-medium text-navy-700">
                                            {r.user_email || "—"}
                                        </div>
                                        <div className="text-[11px] text-navy-400 font-mono">
                                            {r.user_id}
                                        </div>
                                    </td>
                                    <td className="px-5 py-4">
                                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200 uppercase tracking-widest">
                                            {r.field}
                                        </span>
                                    </td>
                                    <td className="px-5 py-4 text-[12px] font-mono text-red-600 break-all max-w-[200px]">
                                        {String(r.sent_value ?? "—")}
                                    </td>
                                    <td className="px-5 py-4 text-[12px] font-mono text-navy-600 break-all max-w-[200px]">
                                        {String(r.expected_value ?? "—")}
                                    </td>
                                    <td className="px-5 py-4 text-[11px] text-navy-500">
                                        {r.kind && (
                                            <div>
                                                <span className="text-navy-400">kind:</span>{" "}
                                                <span className="font-mono">{r.kind}</span>
                                            </div>
                                        )}
                                        {r.item_id && (
                                            <div>
                                                <span className="text-navy-400">item:</span>{" "}
                                                <span className="font-mono">{r.item_id}</span>
                                            </div>
                                        )}
                                        {r.lookup_key && (
                                            <div>
                                                <span className="text-navy-400">lookup:</span>{" "}
                                                <span className="font-mono">{r.lookup_key}</span>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
